import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, AlertCircle, ShieldAlert, Key, ShieldCheck, Copy, Check } from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import type { SignInFlowResult, TotpSetupDetails } from '../components/AuthProvider';
import { validateEmail, validatePassword, FieldErrors, isFormValid } from '../utils/validation';
import { config, getEnvironmentColor } from '../config';

type AuthStep = 'SIGN_IN' | 'NEW_PASSWORD' | 'TOTP_SETUP' | 'TOTP_CODE';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpSetup, setTotpSetup] = useState<TotpSetupDetails | null>(null);
  const { signIn, completeNewPassword, confirmTotpCode, error: authError } = useAuth();
  // Shows why the session ended (e.g. the idle sign-out) until the next attempt.
  const [error, setError] = useState(authError ?? '');
  // The session can also end while this page is open (an authenticated admin
  // may navigate to /login), so pick up a later reason too.
  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean; newPassword?: boolean; confirmPassword?: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [authStep, setAuthStep] = useState<AuthStep>('SIGN_IN');
  const [isSetupCopied, setIsSetupCopied] = useState(false);
  const navigate = useNavigate();

  const isCognitoConfigured = Boolean(config.cognito.userPoolId && config.cognito.userPoolClientId);
  const allowDemoMode = config.environment === 'dev';
  const authConfigurationMissing = !isCognitoConfigured && !allowDemoMode;

  const validateField = (field: 'email' | 'password', value: string) => {
    const result = field === 'email' ? validateEmail(value) : validatePassword(value);
    setFieldErrors(prev => ({
      ...prev,
      [field]: result.error || '',
    }));
    return result.isValid;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      validateField('email', value);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      validateField('password', value);
    }
  };

  const handleBlur = (field: 'email' | 'password') => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, field === 'email' ? email : password);
  };

  const handleSignInStep = (result: SignInFlowResult) => {
    switch (result.step) {
      case 'DONE':
        navigate('/dashboard');
        return;
      case 'NEW_PASSWORD_REQUIRED':
        setAuthStep('NEW_PASSWORD');
        return;
      case 'TOTP_SETUP':
        setTotpSetup(result.totpSetup);
        setTotpCode('');
        setAuthStep('TOTP_SETUP');
        return;
      case 'TOTP_CODE':
        setTotpCode('');
        setAuthStep('TOTP_CODE');
        return;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    const emailValid = validateField('email', email);
    const passwordValid = validateField('password', password);
    setTouched({ email: true, password: true });

    if (!emailValid || !passwordValid) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await signIn(email.trim(), password);
      handleSignInStep(result);
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await completeNewPassword(newPassword, email.trim());
      handleSignInStep(result);
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const normalizedCode = totpCode.replace(/\D/g, '').slice(0, 6);
    if (!/^\d{6}$/.test(normalizedCode)) {
      setError('Enter the 6-digit authenticator code');
      return;
    }

    setIsLoading(true);

    try {
      const result = await confirmTotpCode(normalizedCode, email.trim());
      handleSignInStep(result);
    } catch (err: any) {
      setError(err.message || 'Failed to verify authenticator code');
    } finally {
      setIsLoading(false);
    }
  };

  const copyTotpSetupKey = async () => {
    if (!totpSetup?.sharedSecret) {
      return;
    }

    await navigator.clipboard.writeText(totpSetup.sharedSecret);
    setIsSetupCopied(true);
    setTimeout(() => setIsSetupCopied(false), 2000);
  };

  const handleTotpCodeChange = (value: string) => {
    setTotpCode(value.replace(/\D/g, '').slice(0, 6));
  };

  const canSubmit = email.trim() && password && isFormValid(fieldErrors);
  const canSubmitNewPassword = newPassword.length >= 8 && newPassword === confirmPassword;
  const canSubmitTotp = /^\d{6}$/.test(totpCode.replace(/\D/g, '').slice(0, 6));
  const isTotpSetupStep = authStep === 'TOTP_SETUP';

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4">
            <span className="text-white font-bold text-2xl">DM</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">DonateMate CMS</h1>
          <p className="text-gray-text">Sign in to manage your content</p>
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${getEnvironmentColor()}20`, color: getEnvironmentColor() }}>
            {config.environment.toUpperCase()}
          </div>
        </div>

        <div className="glass-card p-8 animate-spring-in">
          {authStep === 'NEW_PASSWORD' ? (
            <form onSubmit={handleNewPasswordSubmit} className="space-y-6">
              <div className="text-center mb-4">
                <Key className="w-12 h-12 text-primary mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-white">Set New Password</h2>
                <p className="text-sm text-gray-text mt-1">Please create a new password for your account</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div>
                <label className="label">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="input w-full"
                  autoFocus
                />
                <p className="text-xs text-gray-text mt-1">Must be at least 8 characters</p>
              </div>

              <div>
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="input w-full"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !canSubmitNewPassword}
                className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Setting password...</span>
                  </>
                ) : (
                  <>
                    <Key className="w-5 h-5" />
                    <span>Set Password</span>
                  </>
                )}
              </button>
            </form>
          ) : authStep === 'TOTP_SETUP' || authStep === 'TOTP_CODE' ? (
            <form onSubmit={handleTotpSubmit} className="space-y-6">
              <div className="text-center mb-4">
                <ShieldCheck className="w-12 h-12 text-primary mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-white">
                  {isTotpSetupStep ? 'Set Up Authenticator' : 'Authenticator Code'}
                </h2>
                <p className="text-sm text-gray-text mt-1">
                  {isTotpSetupStep
                    ? 'Add this setup key to your authenticator app, then enter the generated code'
                    : 'Enter the 6-digit code from your authenticator app'}
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {isTotpSetupStep && totpSetup && (
                <div>
                  <label className="label">Setup Key</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={totpSetup.sharedSecret}
                      className="input flex-1 font-mono text-sm"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={copyTotpSetupKey}
                      className="btn-secondary px-4 flex items-center justify-center"
                      aria-label="Copy setup key"
                    >
                      {isSetupCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="label">Authenticator Code</label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => handleTotpCodeChange(e.target.value)}
                  placeholder="123456"
                  className="input w-full tracking-widest"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !canSubmitTotp}
                className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    <span>{isTotpSetupStep ? 'Verify Authenticator' : 'Verify Code'}</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {!isCognitoConfigured && allowDemoMode && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start space-x-3">
                  <ShieldAlert className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-400 font-medium">Demo Mode</p>
                    <p className="text-xs text-yellow-400/80 mt-1">
                      Cognito is not configured. Using mock authentication.
                    </p>
                  </div>
                </div>
              )}

              {authConfigurationMissing && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start space-x-3">
                  <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-400 font-medium">Authentication is not configured</p>
                    <p className="text-xs text-red-400/80 mt-1">
                      Admin Cognito settings are required outside development.
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => handleBlur('email')}
                  placeholder="admin@donatemate.com"
                  className={`input w-full ${touched.email && fieldErrors.email ? 'border-red-500' : ''}`}
                  autoFocus
                />
                {touched.email && fieldErrors.email && (
                  <p className="text-sm text-red-400 mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <label className="label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => handleBlur('password')}
                  placeholder="••••••••••••"
                  className={`input w-full ${touched.password && fieldErrors.password ? 'border-red-500' : ''}`}
                />
                {touched.password && fieldErrors.password && (
                  <p className="text-sm text-red-400 mt-1">{fieldErrors.password}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !canSubmit || authConfigurationMissing}
                className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
          )}

          {!isCognitoConfigured && allowDemoMode && authStep === 'SIGN_IN' && (
            <div className="mt-6 pt-6 border-t border-gray-border">
              <p className="text-sm text-gray-text text-center">
                Configure Cognito in your environment file to enable secure authentication.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-sm text-gray-text">
          <p>© 2025 DonateMate. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
